<template>
  <gc-main-layout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :tabs="tabs"
    :selectedIndex="selectedIndex"
    :baseView="baseView"
    :key="pageKey"
    :formsConfig="formsConfig"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    class="grower-tab"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        title="Supplier"
        :baseView="baseView"
        :primaryLabel="model.Name"
        :secondaryLabel="baseView.id"
        recordType="Supplier"
      />
    </template>
    <div class="css-v2 gc-master-screen">
      <DxToolbar ref="toolbarRef" class="view-toolbar">
        <DxItem
          :options="toolbar.edit"
          location="after"
          locate-in-menu="auto"
          widget="dxButton"
          v-if="baseView.mode == 'review'"
        />
        <DxItem
          :options="toolbar.save"
          location="after"
          locate-in-menu="auto"
          widget="dxButton"
          v-if="baseView.mode != 'review'"
        />
      </DxToolbar>
      <div class="view-card responsive-paddings grower-tab-card">
        <form v-if="dataLoaded" autocomplete="new-dxform-grower-details">
          <DxForm
            :form-data="model"
            :read-only="false"
            :show-colon-after-label="false"
            :show-required-mark="false"
            :show-validation-summary="false"
            :validation-group="formsConfig.validationGroup"
            label-location="left"
            :items="growerDetailFormItems"
          />
        </form>
        <div
          v-if="dataLoaded"
          class="dx-form-group-caption grower-huller-grid-caption"
        >
          Huller Associations
        </div>
        <DxDataGrid
          v-if="dataLoaded"
          ref="hullerGrid"
          :data-source="hullerAssociations"
          :columns="hullerColumns"
          key-expr="HullerCode"
          :show-borders="true"
          :column-auto-width="true"
          no-data-text="No active Huller sites found."
          @editor-preparing="onEditorPreparing"
          @toolbar-preparing="onGridToolbarPreparing"
        >
          <DxColumnChooser :enabled="true" mode="select" />
          <DxEditing
            mode="batch"
            :allow-updating="isEditing"
            :allow-adding="false"
            :allow-deleting="false"
          />
          <template #hullerCellTemplate="{ data }">
            <GcRecordCell
              :data="data"
              :primary="[{ dataField: 'HullerName' }]"
              :secondary="[{ dataField: 'HullerCode' }]"
            />
          </template>
        </DxDataGrid>
      </div>
    </div>
  </gc-main-layout>
</template>

<script>
import BaseViewMixin from "../../v1/_mixins/base.view";
import validationEngine from "devextreme/ui/validation_engine";
import {
  DxDataGrid,
  DxEditing,
  DxColumnChooser,
} from "devextreme-vue/data-grid";
import { FeatureStorage } from "../../_services/storage";
import { SupplierService } from "../../_services/api/suppliers/supplier.service";
import { AlmondcoGrowerDetailService } from "../../_services/api/suppliers/almondco-grower-detail.service";
import {
  SupplierDetailTabs,
  SupplierDetailTabsVisibility,
  IsGrowerTabVisible,
  GetIndexByTabName,
} from "../../_models/tabdata/suppliers/supplier-detail-tabs";

export default {
  components: { DxDataGrid, DxEditing, DxColumnChooser },
  mixins: [BaseViewMixin],
  async created() {
    this.showLoading(true);
    await SupplierService.getByCode(this.baseView.id).then(async (response) => {
      if (response.data.value.length > 0) {
        this.model.Name = response.data.value[0].Name;
        // Bumps pageKey (baseView.mode + this.key), forcing gc-main-layout's
        // keyed subtree - including the deeply-nested GcHeaderContentDynamic
        // inside gc-header-toolbar.vue's own DevExtreme dx-toolbar/dx-item
        // structure - to remount with the now-correct Name. Plain prop
        // reactivity alone doesn't reach that deep; matches
        // supplier-detail.vue's own identical call in its equivalent spot.
        this.refreshView();
        // Landing here directly (deep link, hard refresh, bookmark) skips
        // supplier-detail.vue's created() entirely, so growerTabVisible is
        // still its module-level default (false) unless this route also
        // computes it itself - same reason every product-detail.vue sibling
        // tab (product-cost.vue, product-location.vue, etc.) independently
        // calls ProductTabsVisibility instead of relying on product-detail.vue
        // having run first.
        SupplierDetailTabsVisibility({
          text: "Grower",
          value: await IsGrowerTabVisible(response.data.value[0].SupplierTypeCode),
        });
      }
      await Promise.all([
        this.loadGrowerRegions(),
        this.loadCurrentSeasonAndSchemes(),
        this.loadGrowerDetail(),
        this.loadHullerAssociations(),
      ]);
      this.dataLoaded = true;
      this.showLoading(false);
    });
  },
  data() {
    return {
      model: {
        Name: "",
        SupplierCode: "",
        GrowerType: "",
        SchemeId: "",
        Region: "",
        Location: "",
        RecordId: null,
      },
      dataLoaded: false,
      growerTypes: [
        { Code: "I", Description: "Individual" },
        { Code: "S", Description: "Syndicate" },
        { Code: "P", Description: "Pool" },
      ],
      growerRegions: [],
      schemeOptions: [],
      currentSeasonYear: undefined,
      hullerAssociations: [],
      toolbar: {
        edit: {
          icon: "edit",
          // §6 says checkPermission('Supplier', 'update') - not a
          // create-vs-update split, this tab has no create state of its own.
          // But the real FeatureStorage.checkPermission(featureCode, mode)
          // only recognises mode "create"/"edit"/"review" (feature.storage.js)
          // - "update" matches none of them and always returns false. Using
          // "edit" here instead, which is this function's real name for the
          // same WritePermission check the design's wording describes.
          // Feature code repointed to its own dedicated AlmondcoGrower (ADO
          // #78320, 2026-09-16) - previously reused the base Supplier code,
          // matching the API-side Save_AlmondcoGrowerDetailController repoint.
          visible: FeatureStorage.checkPermission("AlmondcoGrower", "edit"),
          onClick: () => {
            this.baseView.mode = "edit";
          },
        },
        save: {
          icon: "floppy",
          visible: FeatureStorage.checkPermission("AlmondcoGrower", "edit"),
          onClick: () => {
            this.saveGrowerDetail();
          },
        },
      },
    };
  },
  computed: {
    // §6: both sections switch into Edit mode together, off the same
    // baseView.mode toggle - matches inshell-product.vue's own identical
    // isEditing computed for a per-row-conditional-editability grid.
    isEditing() {
      return this.baseView.mode === "edit";
    },
    tabs() {
      // Gating on dataLoaded is not cosmetic - see supplier-detail.vue's
      // tabs() for why this is required for the visibility flag set above
      // to ever actually reach the rendered tab strip.
      if (this.dataLoaded) return SupplierDetailTabs(this.baseView.id);
      else return [];
    },
    growerDetailFormItems() {
      // Always dxSelectBox (never swapped to dxTextBox in review mode) so the
      // dropdown affordance stays visible at all times - disabled (not
      // editorType-swapped) is what actually locks the field in review mode,
      // matching supplier-detail.vue's own Supplier Type field behaviour.
      return [
        {
          itemType: "group",
          caption: "Grower Details",
          colCount: 2,
          items: this.parseFormColumns([
            {
              dataField: "GrowerType",
              gcLabel: "Type",
              gcType: "String",
              required: true,
              editorType: "dxSelectBox",
              disabled: this.baseView.mode == "review",
              editorOptions: {
                dataSource: this.growerTypes,
                valueExpr: "Code",
                displayExpr: "Description",
                searchEnabled: true,
                onValueChanged: this.onGrowerTypeChanged,
              },
            },
            {
              dataField: "SchemeId",
              gcLabel: "Price Scheme",
              gcType: "String",
              required: this.model.GrowerType == "I",
              editorType: "dxSelectBox",
              disabled: this.baseView.mode == "review" || this.model.GrowerType != "I",
              editorOptions: {
                dataSource: this.schemeOptions,
                valueExpr: "SchemeId",
                displayExpr: "SchemeId",
                searchEnabled: true,
              },
            },
            {
              dataField: "Region",
              gcLabel: "Region",
              gcType: "String",
              editorType: "dxSelectBox",
              disabled: this.baseView.mode == "review",
              editorOptions: {
                dataSource: this.growerRegions,
                valueExpr: "GrowerRegionCode",
                searchEnabled: true,
                searchExpr: ["GrowerRegionCode", "GrowerRegionName"],
                // ui-ux-guidelines.md coded-lookup convention: "Code -
                // Description" in the field after selection,
                // description-primary/code-secondary in the dropdown list -
                // matches customer-receipt.vue's real displayExpr-function +
                // itemTemplate-function pattern exactly.
                displayExpr: (item) =>
                  item ? `${item.GrowerRegionCode} - ${item.GrowerRegionName}` : "",
                itemTemplate: (data) => {
                  return `<div class="custom-auto-complete">
                            <span class="auto-complete-primary">${data.GrowerRegionName}</span>
                            <span class="auto-complete-secondary">${data.GrowerRegionCode}</span>
                          </div>`;
                },
              },
            },
            {
              dataField: "Location",
              gcLabel: "Location",
              gcType: "String",
              disabled: this.baseView.mode == "review",
              editorOptions: { maxLength: 10 },
            },
          ]),
        },
      ];
    },
    hullerColumns() {
      return [
        {
          dataField: "Include",
          caption: "",
          dataType: "boolean",
          alignment: "left",
          width: 40,
          allowEditing: this.isEditing,
          allowHiding: false,
        },
        {
          dataField: "HullerName",
          caption: "Huller",
          minWidth: 220,
          allowEditing: false,
          allowHiding: false,
          cellTemplate: "hullerCellTemplate",
        },
        {
          dataField: "HullerCode",
          caption: "Code",
          visible: false,
          showInColumnChooser: true,
          allowEditing: false,
        },
        {
          dataField: "HullerName",
          caption: "Description",
          visible: false,
          showInColumnChooser: true,
          allowEditing: false,
        },
        {
          dataField: "DollarsPerKg",
          caption: "Cracking Rate ($/Kg)",
          dataType: "number",
          format: "#,##0.00",
          alignment: "right",
          allowEditing: this.isEditing,
          editorOptions: { format: "#,##0.00" },
        },
      ];
    },
  },
  methods: {
    async loadGrowerDetail() {
      await AlmondcoGrowerDetailService.getGrowerDetail(this.baseView.id).then(
        (response) => {
          // §5/§9: an empty result array means "no Grower Detail yet" - a
          // normal state, not an error. Render blank/default header fields.
          if (response.data.value.length > 0) {
            const detail = response.data.value[0];
            this.model.SupplierCode = detail.SupplierCode;
            this.model.GrowerType = detail.GrowerType;
            this.model.SchemeId = detail.SchemeId;
            this.model.Region = detail.Region;
            this.model.Location = detail.Location;
            this.model.RecordId = detail.RecordId;
          } else {
            this.model.SupplierCode = this.baseView.id;
            this.model.GrowerType = "";
            this.model.SchemeId = "";
            this.model.Region = "";
            this.model.Location = "";
            this.model.RecordId = null;
          }
        }
      );
    },
    async loadGrowerRegions() {
      await AlmondcoGrowerDetailService.getGrowerRegions().then((response) => {
        this.growerRegions = response.data.value;
      });
    },
    async loadHullerAssociations() {
      await AlmondcoGrowerDetailService.getHullerAssociations(
        this.baseView.id
      ).then((response) => {
        this.hullerAssociations = response.data.value.map((row) => ({
          HullerCode: row.HullerCode,
          HullerName: row.HullerName,
          Include: row.Status == "A",
          DollarsPerKg: row.DollarsPerKg || 0,
          RecordId: row.RecordId,
        }));
      });
    },
    onEditorPreparing(e) {
      if (e.parentType !== "dataRow") return;
      if (e.dataField !== "DollarsPerKg") return;
      if (!e.row.data.Include) {
        e.editorOptions.disabled = true;
      }
    },
    onGridToolbarPreparing(e) {
      e.toolbarOptions.items = e.toolbarOptions.items.filter(
        (item) => item.name !== "saveButton" && item.name !== "revertButton"
      );
    },
    async loadCurrentSeasonAndSchemes() {
      // "Current FY" resolved the same way console-season-year.vue's own
      // setDefaultSeasonYear does (the AlmondcoIntakeSeason row whose date
      // range contains today) - reuse-before-create, not a second
      // definition of "current fiscal year".
      await AlmondcoGrowerDetailService.getIntakeSeasons().then(async (response) => {
        const today = new Date();
        const currentSeason = (response.data.value || []).find(
          (season) =>
            new Date(season.StartDate) <= today && today <= new Date(season.EndDate)
        );
        if (!currentSeason) return;
        this.currentSeasonYear = currentSeason.Season;
        await AlmondcoGrowerDetailService.getSchemePricesForSeason(
          this.currentSeasonYear
        ).then((schemeResponse) => {
          const distinctSchemeIds = [
            ...new Set(schemeResponse.data.value.map((row) => row.SchemeId)),
          ].sort();
          this.schemeOptions = distinctSchemeIds.map((schemeId) => ({
            SchemeId: schemeId,
          }));
        });
      });
    },
    onGrowerTypeChanged(e) {
      // BR-04: SchemeId is editable only when GrowerType = 'I' - cleared
      // client-side immediately on change, independently re-checked
      // server-side (BR-05).
      if (e.value != "I") {
        this.model.SchemeId = null;
      }
    },
    async saveGrowerDetail() {
      const result = await validationEngine.validateGroup(
        this.formsConfig.validationGroup
      );
      if (!result.isValid) {
        this.addErrorMessagesFromValidationResult({
          brokenRules: result.brokenRules,
        });
        this.displayCustomValidationSummary();
        return;
      }

      await this.$refs.hullerGrid.instance.saveEditData();

      this.showLoading(true);
      const payload = {
        SupplierCode: this.baseView.id,
        GrowerType: this.model.GrowerType,
        SchemeId: this.model.GrowerType == "I" ? this.model.SchemeId : null,
        Region: this.model.Region,
        Location: this.model.Location,
        HullerAssociations: this.hullerAssociations.map((row) => ({
          HullerCode: row.HullerCode,
          DollarsPerKg: row.DollarsPerKg,
          Status: row.Include ? "A" : "X",
          RecordId: row.RecordId,
        })),
      };
      await AlmondcoGrowerDetailService.saveGrowerDetail(payload)
        .then(async () => {
          this.$notify(
            `Grower Details for ${this.model.Name} saved successfully`,
            "success"
          );
          this.baseView.mode = "review";
          await Promise.all([this.loadGrowerDetail(), this.loadHullerAssociations()]);
          this.showLoading(false);
        })
        .catch((error) => {
          this.showLoading(false);
          this.displayServerError(error, "Grower Details save failed");
        });
    },
    backButtonFunction() {
      this.$router.push({
        path: `/supplier/review/${this.baseView.id}`,
      });
    },
  },
  mounted() {
    // Looked up by name, not hardcoded, since the tab strip's order lives in
    // the shared supplier-detail-tabs.js module and can be reordered there
    // independently of this file.
    this.selectedIndex = GetIndexByTabName("Grower");
  },
};
</script>
<style lang="scss">
.grower-tab {
  .grower-tab-card {
    margin-top: 12px !important;
  }
  .grower-huller-grid-caption {
    margin-top: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #e0e0e0;
  }
}
</style>
